/**
 * WidgetChatContainer - ウィジェット用チャットコンテナ
 *
 * FloatingWidget内で使用される、コンパクトなチャットUI。
 * ChatContainerのウィジェット版。
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { ChatInput } from "./ChatInput";
import { MessageList } from "./MessageList";
import { type Message } from "./MessageBubble";
import { type FeedbackRating } from "./FeedbackBar";
import { cn } from "../../utils";

type Mode = "medical_fee" | "facility_standard";

const normalizeWhitespace = (value: string) => value.replace(/\s+/g, " ").trim();

type PersistedChatStateV1 = {
	v: 1;
	sessionId?: string;
	messages: Message[];
	savedAt: number;
};

const chatStorageKey = (userId: string, mode: Mode) => `shirobon_ai_widget_chat:${userId}:${mode}`;

const safeLoadJson = <T,>(key: string): T | null => {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.localStorage.getItem(key);
		if (!raw) return null;
		return JSON.parse(raw) as T;
	} catch {
		return null;
	}
};

const safeSaveJson = (key: string, value: unknown) => {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(key, JSON.stringify(value));
	} catch {
		// ignore (storage full / blocked)
	}
};

interface ChatResponse {
	answer: string;
	sources?: Array<{
		code?: string;
		title?: string;
		url?: string;
		type?: string;
	}>;
	similar_questions?: Array<{
		doc_id?: string;
		title?: string;
		url?: string;
	}>;
	session_id?: string;
	guidance_type?: string;
	suggestions?: Array<{ label: string; description?: string; query?: string }>;
	debug?: {
		mode?: string;
	};
}

const buildCitationsFromSources = (sources: ChatResponse["sources"] | undefined) => {
	if (!sources || sources.length === 0) return [];

	// 1) Dedupe identical sources (type + url + code)
	const deduped: Array<NonNullable<ChatResponse["sources"]>[number]> = [];
	const seen = new Set<string>();
	for (const s of sources) {
		const url = s.url ?? "";
		const key = `${s.type ?? ""}|${url}|${s.code ?? ""}`;
		if (seen.has(key)) continue;
		seen.add(key);
		deduped.push(s);
	}

	// 2) Append 1..N to labels when same code repeats
	const byCode = new Map<string, number>();
	for (const s of deduped) {
		const code = (s.code || "").trim();
		if (!code) continue;
		byCode.set(code, (byCode.get(code) ?? 0) + 1);
	}
	const currentIndex = new Map<string, number>();

	return deduped.map((source, index) => {
		const code = source.code || `資料${index + 1}`;
		const baseLabel = normalizeWhitespace(source.title || source.url || "参考リンク");
		const total = byCode.get(code) ?? 0;
		if (total <= 1) {
			return {
				code,
				label: baseLabel,
				url: source.url,
				type: source.type as "score" | "doubt" | "qa" | "facility" | undefined,
			};
		}
		const next = (currentIndex.get(code) ?? 0) + 1;
		currentIndex.set(code, next);
		return {
			code,
			label: `${baseLabel} ${next}`,
			url: source.url,
			type: source.type as "score" | "doubt" | "qa" | "facility" | undefined,
		};
	});
};

interface RefineQueryResponse {
	refined_query: string;
}

export interface WidgetChatContainerProps {
	/** API endpoint URL */
	apiUrl: string;
	/** User ID for session tracking */
	userId: string;
	/** Chat mode */
	mode?: Mode;
}

export function WidgetChatContainer({
	apiUrl,
	userId,
	mode: initialMode = "medical_fee",
}: WidgetChatContainerProps) {
	const [mode, setMode] = useState<Mode>(initialMode);
	const restored = safeLoadJson<PersistedChatStateV1>(chatStorageKey(userId, initialMode));
	const [sessionId, setSessionId] = useState<string | undefined>(restored?.v === 1 ? restored.sessionId : undefined);
	const [messages, setMessages] = useState<Message[]>(restored?.v === 1 ? restored.messages : []);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const hasSentWelcomeRef = useRef(false);

	const sendMessage = useCallback(async (text: string): Promise<ChatResponse | null> => {
		const trimmed = text.trim();
		if (!trimmed) return null;

		setIsLoading(true);
		setError(null);

		try {
			const response = await fetch(apiUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					question: trimmed,
					user_id: userId,
					mode,
					session_id: sessionId,
				}),
			});

			if (!response.ok) {
				throw new Error(`HTTP error: ${response.status}`);
			}

			const data: ChatResponse = await response.json();
			return data;
		} catch (err) {
			const message = err instanceof Error ? err.message : "通信エラーが発生しました";
			setError(message);
			return null;
		} finally {
			setIsLoading(false);
		}
	}, [apiUrl, userId, mode, sessionId]);

	// Persist chat state (history + session) so it survives page navigation.
	useEffect(() => {
		const payload: PersistedChatStateV1 = {
			v: 1,
			sessionId,
			messages,
			savedAt: Date.now(),
		};
		safeSaveJson(chatStorageKey(userId, mode), payload);
	}, [userId, mode, sessionId, messages]);

	const handleSend = useCallback(async (text: string) => {
		const trimmed = text.trim();
		if (!trimmed) return;

		// Add user message
		const userMsg: Message = {
			id: `user-${Date.now()}`,
			role: "user",
			content: trimmed,
		};
		setMessages((prev) => [...prev, userMsg]);

		// Send to API
		const data = await sendMessage(trimmed);

		if (data) {
			setSessionId(data.session_id);

			// Update mode from backend if detected
			if (data.debug?.mode) {
				const detectedMode = data.debug.mode as Mode;
				if (detectedMode === "medical_fee" || detectedMode === "facility_standard") {
					setMode(detectedMode);
				}
			}

			// Build citations from sources
			const sourceCitations = buildCitationsFromSources(data.sources);

			// Add similar_questions as qa-type citations
			const qaCitations = data.similar_questions?.map((sq) => ({
				code: sq.doc_id || "",
				label: sq.title || "関連Q&A",
				url: sq.url,
				type: "qa" as const,
			})) ?? [];

			const aiMsg: Message = {
				id: `assistant-${Date.now()}`,
				role: "assistant",
				content: data.answer,
				citations: [...sourceCitations, ...qaCitations],
				guidance_type: data.guidance_type,
				suggestions: data.suggestions,
			};
			setMessages((prev) => [...prev, aiMsg]);
		} else if (error) {
			// Add error message
			const errorMsg: Message = {
				id: `error-${Date.now()}`,
				role: "assistant",
				content: `エラーが発生しました: ${error}`,
			};
			setMessages((prev) => [...prev, errorMsg]);
		}
	}, [sendMessage, error]);

	const handleFeedbackSubmit = useCallback(async (messageId: string, rating: FeedbackRating, comment: string) => {
		// Feedback submission
		try {
			const feedbackUrl = apiUrl.replace(/\/chat\/?$/, "/feedback");
			await fetch(feedbackUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					query_id: messageId,
					rating,
					comment,
					user_id: userId,
				}),
			});
		} catch (err) {
			console.error("[ShirobonAI] Feedback submission error:", err);
		}
	}, [apiUrl, userId]);

	const refineQueryWithLLM = useCallback(async (originalQuestion: string, suggestionType: string): Promise<string | null> => {
		try {
			const response = await fetch(apiUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					action: "refine_query",
					original_question: originalQuestion,
					suggestion_type: suggestionType,
					mode,
				}),
			});

			if (!response.ok) {
				return null;
			}

			const data: RefineQueryResponse = await response.json();
			return data.refined_query || null;
		} catch (err) {
			console.error("[ShirobonAI] refineQuery error:", err);
			return null;
		}
	}, [apiUrl, mode]);

	const handleGuidanceSelect = useCallback(async (selection: { label: string; query?: string }) => {
		if (!selection.query) return;

		// Get the last user message to use as context
		const lastUserMessage = [...messages].reverse().find((m) => m.role === "user");
		const originalQuestion = lastUserMessage?.content || "";

		// 1. 即座にローディング表示（TypingIndicator）
		const tempMsgId = `user-generating-${Date.now()}`;
		const generatingMsg: Message = {
			id: tempMsgId,
			role: "user",
			content: "",
			isGenerating: true,
		};
		setMessages((prev) => [...prev, generatingMsg]);

		// 2. LLMでクエリ生成
		const refinedQuery = await refineQueryWithLLM(originalQuestion, selection.label);

		if (refinedQuery) {
			// 3. ローディングを実際のクエリに置換
			setMessages((prev) =>
				prev.map((msg) =>
					msg.id === tempMsgId
						? { ...msg, content: refinedQuery, isGenerating: false }
						: msg
				)
			);

			// 4. APIに送信
			const data = await sendMessage(refinedQuery);
			if (data) {
				setSessionId(data.session_id);

				const sourceCitations = buildCitationsFromSources(data.sources);

				const qaCitations = data.similar_questions?.map((sq) => ({
					code: sq.doc_id || "",
					label: sq.title || "関連Q&A",
					url: sq.url,
					type: "qa" as const,
				})) ?? [];

				const aiMsg: Message = {
					id: `assistant-${Date.now()}`,
					role: "assistant",
					content: data.answer,
					citations: [...sourceCitations, ...qaCitations],
					guidance_type: data.guidance_type,
					suggestions: data.suggestions,
				};
				setMessages((prev) => [...prev, aiMsg]);
			}
		} else {
			// フォールバック: 従来のテンプレートクエリを使用
			const fallbackQuery = selection.query;
			setMessages((prev) =>
				prev.map((msg) =>
					msg.id === tempMsgId
						? { ...msg, content: fallbackQuery, isGenerating: false }
						: msg
				)
			);

			const data = await sendMessage(fallbackQuery);
			if (data) {
				setSessionId(data.session_id);

				const sourceCitations = buildCitationsFromSources(data.sources);

				const qaCitations = data.similar_questions?.map((sq) => ({
					code: sq.doc_id || "",
					label: sq.title || "関連Q&A",
					url: sq.url,
					type: "qa" as const,
				})) ?? [];

				const aiMsg: Message = {
					id: `assistant-${Date.now()}`,
					role: "assistant",
					content: data.answer,
					citations: [...sourceCitations, ...qaCitations],
					guidance_type: data.guidance_type,
					suggestions: data.suggestions,
				};
				setMessages((prev) => [...prev, aiMsg]);
			}
		}
	}, [messages, refineQueryWithLLM, sendMessage]);

	// Send welcome message on mount
	useEffect(() => {
		// If we already restored a session/history, do not send an automatic welcome.
		if (messages.length > 0 || sessionId) return;
		if (hasSentWelcomeRef.current) return;
		hasSentWelcomeRef.current = true;

		// Delay welcome message slightly to avoid blocking initial render
		const timer = setTimeout(() => {
			handleSend("こんにちは");
		}, 500);

		return () => clearTimeout(timer);
	}, [handleSend]);

	const hasUserMessage = messages.some((m) => m.role === "user");
	const showLoading = isLoading && hasUserMessage;
	const scrollKey = `${userId}:${mode}`;

	return (
		<div className="flex h-full flex-col">
			{/* Messages Area */}
			<div className="flex-1 overflow-y-auto bg-gradient-to-br from-slate-50 to-white p-2">
				<MessageList
					messages={messages}
					isLoading={showLoading}
					onFeedbackSubmit={handleFeedbackSubmit}
					onGuidanceSelect={handleGuidanceSelect}
					persistScrollKey={scrollKey}
				/>
			</div>

			{/* Input Area */}
			<div className="border-t border-slate-200 bg-white p-2">
				<ChatInput onSend={handleSend} disabled={isLoading} />
				<p className="mt-1 text-center text-slate-400" style={{ fontSize: '12px' }}>
					回答は参考情報です。最終判断は公式資料をご確認ください。
				</p>
			</div>
		</div>
	);
}

